import * as React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Link, useNavigate } from 'react-router-dom';

import { Box, CircularProgress, Grid, Skeleton, Stack, Typography } from '@mui/material';

import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ShowFnOutput, useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';

import { venueGetConversationWithUserApi } from '../../api/chat/venueGetConversationWithUserApi';
import { venueUpdateInvitePaymentToReleaseApi } from '../../api/invites/venueUpdateInvitePaymentToReleaseApi';
import { venueUpdateInvitePaymentToStopApi } from '../../api/invites/venueUpdateInvitePaymentToStopApi';
import LazyAvatar from '../../components/Avatar/LazyAvatar/LazyAvatar';
import Button from '../../components/Button/Button';
import LoadingButton from '../../components/Button/LoadingButton';
import SaveUserButton from '../../components/Button/SaveUserButton';
import { AddTrackingNumberDialog } from '../../components/Dialogs/AddTrackingNumber/AddTrackingNumberDialog';
import { BaseDialogProps } from '../../components/Dialogs/Base';
import CancelReservationDialog from '../../components/Dialogs/CancelReservation/CancelReservationDialog';
import { CheckedInConfirmDialog } from '../../components/Dialogs/Confirm/CheckedInConfirmDialog';
import { PaymentConfirmDialog } from '../../components/Dialogs/Confirm/PaymentConfirmDialog';
import { ExtendedOfferRequirements } from '../../components/Dialogs/CreateInvite/context/types';
import { EditReservationDateAndTimeDialog } from '../../components/Dialogs/EditReservationDateAndTime/EditReservationDateAndTimeDialog';
import { ExtendPostingTimeDialog } from '../../components/Dialogs/ExtendPostingTime/ExtendPostingTimeDialog';
import ReportReservationsDialog from '../../components/Dialogs/ReportReservations/ReportReservationsDialog';
import { ReservationRatingDialog } from '../../components/Dialogs/ReservationRating/ReservationRatingDialog';
import FloatTopLeft from '../../components/FloatTopLeft/FloatTopLeft';
import GoBack from '../../components/GoBack/GoBack';
import InstagramTag from '../../components/InstagramTag/InstagramTag';
import LabelDetail from '../../components/LabelDetail/LabelDetail';
import { ReservationPostLink } from '../../components/ReservationPostLink';
import { ReservationTimeLeftToPost } from '../../components/ReservationTimeLeftToPost';
import SkeletonWithChild from '../../components/SkeletonWithChild/SkeletonWithChild';
import StatusLabel from '../../components/StatusLabel/StatusLabel';
import { ExtendedReservationStatus } from '../../components/Tables/Reservation/Reservation';
import { useReservationLimitInPeriodContext } from '../../contexts/ReservationsLimitInPeriodContext';
import {
  OfferCategory,
  OfferRequirements,
  Reservation,
  ReservationReviewType,
  ReservationStatus,
  ShippingStatus,
  UserUpdateStatus,
} from '../../graphql/API';
import { useQueryUser } from '../../hooks/reactQuery/useQueryUser';
import useVenueId from '../../hooks/useVenueId';
import { useAppSelector } from '../../redux/hooks';
import Icons from '../../themes/icons';
import {
  getDateAndTimeInTimezoneInDayjs,
  getTimeInTimezoneInDayjs,
  isAfterNow,
} from '../../utils/dateV2';
import { formatNumber } from '../../utils/formatter';
import { OfferCategoriesCallToConfirm } from '../../utils/offerAccess';
import {
  checkChatHoursIsNotPassed,
  getReservationRequirementsValues,
  getReservationStatus,
  isAfter48HoursOfOfferEnd,
} from '../../utils/reservations';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { useRequirementOfReservation } from '../OfferDetail/components/useRequirment';
import { extractSubscriptionPlan } from '../SubscriptionPlans/common/helper';
import DateTime from './components/DateTime';
import { OfferPriceStatus } from './components/OfferPriceStatus';
import { ReleasePaymentButton, StopPaymentButton } from './components/PaymentButtons';
import Requirements from './components/Requirements';
import { useUpdateReservationMutation } from './components/useMutation';
import { useQueryReservation } from './components/useQuery';
import { useChatContext } from '../../contexts/ChatContext/useChatContext';

export enum ReservationPost {
  validated = 'validated',
  validationFailed = 'validationFailed',
  validationUpdated = 'validationUpdated',
}

export enum ReservationPaymentStatus {
  released = 'released',
  ready = 'ready',
  stop = 'stop',
  processed = 'processed',
}

export const ReservationPostTypePostingTimeLimitInHours: Record<string, number> = {
  [OfferRequirements.postOnInstagramFeed]: 72,
  [ExtendedOfferRequirements.noRequirement]: 0,
  [OfferRequirements.postOnInstagramReels]: 72,
  [OfferRequirements.postOnInstagramStories3x]: 12,
  [OfferRequirements.postOnInstagramStories]: 12,
};

const getAddressString = (reservation?: Reservation) => {
  if (!reservation?.shippingAddress) {
    return '';
  }
  return [
    reservation.shippingAddress.address1,
    reservation?.shippingAddress.address2,
    reservation?.shippingAddress.city,
    reservation?.shippingAddress.postalCode + ' ' + reservation?.shippingAddress.stateProvince,
    reservation?.shippingAddress.country,
  ]
    .filter((i) => !!i)
    .join(', ');
};

const ReservationDetail: React.FC = () => {
  const navigate = useNavigate();
  const venueId = useVenueId();
  const { enqueueSnackbar } = useSnackbar();
  const venue = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);
  const { data, isLoading, refetch } = useQueryReservation();
  const { data: userData } = useQueryUser(venueId, data?.userID);
  const updateReservationMutation = useUpdateReservationMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const timezoneId = React.useMemo(() => data?.offer?.offerTimeZoneId && data?.offer?.category === OfferCategory.event ? data?.offer?.offerTimeZoneId : data?.timeZoneId, [data?.offer?.offerTimeZoneId, data?.offer?.category, data?.timeZoneId]);
  const requirements = useRequirementOfReservation(data ?? undefined);
  const { showModal } = useModal();
  const complainModalRef = React.useRef<ShowFnOutput<BaseDialogProps>>();
  const cancelReservationModalRef = React.useRef<ShowFnOutput<BaseDialogProps>>();
  const shipmentReservationModalRef = React.useRef<ShowFnOutput<BaseDialogProps>>();
  const editDateAndTimeModalRef = React.useRef<ShowFnOutput<BaseDialogProps>>();
  const paymentReleaseDialog = React.useRef<ShowFnOutput<BaseDialogProps>>();
  const paymentStopDialog = React.useRef<ShowFnOutput<BaseDialogProps>>();
  const checkedInDialog = React.useRef<ShowFnOutput<BaseDialogProps>>();
  const extendPostingTimeDialog = React.useRef<ShowFnOutput<BaseDialogProps>>();

  const { getReservationsLimitInPeriodHandle } = useReservationLimitInPeriodContext() || {};

  const [isConversationCreationLoading, setIsConversationCreationLoading] = React.useState(false);

  const { shouldChatOpen, setShouldChatOpen, setReservation } = useChatContext() || {};

  const paymentReleaseMutation = useMutation({
    mutationFn: venueUpdateInvitePaymentToReleaseApi,
    onSuccess: () => {
      refetch();
      enqueueSnackbar('Payment release action submitted successfully', { variant: 'success' });
    },
  });
  const paymentStopMutation = useMutation({
    mutationFn: venueUpdateInvitePaymentToStopApi,
    onSuccess: () => {
      refetch();
      enqueueSnackbar('Payment stop action submitted successfully', { variant: 'success' });
    },
  });

  const { offerStartTime, offerEndTime, checkedInTime, reservedDate } = React.useMemo(() => {
    const reservationStartDate = dayjs.utc(data?.effectiveStartDateTime).format('YYYY-MM-DD');
    const reservationStartTime = dayjs.utc(data?.effectiveStartDateTime).format('HH:mm');
    const reservationEndTime = dayjs.utc(data?.effectiveEndDateTime).format('HH:mm');

    return {
      offerStartTime: getTimeInTimezoneInDayjs(
        reservationStartDate || '',
        reservationStartTime || '',
        timezoneId || '',
      ),
      offerEndTime: getTimeInTimezoneInDayjs(
        reservationStartDate || '',
        reservationEndTime || '',
        timezoneId,
      ),
      checkedInTime: getDateAndTimeInTimezoneInDayjs(data?.checkInDateTime || '', data?.timeZoneId),
      reservedDate: dayjs.utc(data?.effectiveStartDateTime),
    };
  }, [data]);

  const checkedInValue = React.useMemo(() => {
    const reservationStatus = data?.status;
    if (reservationStatus === ReservationStatus.checkedin) {
      return <DateTime value={checkedInTime ?? dayjs()} />;
    }

    if (reservationStatus === ReservationStatus.noshow) {
      return 'User did not check in';
    }

    return 'Not checked in yet';
  }, [checkedInTime, data?.status]);

  const onCancelReservationDialogHandle = () => {
    cancelReservationModalRef.current = showModal(CancelReservationDialog, {
      modalRef: cancelReservationModalRef,
      reservationDetail: data,
      refetchReservationDetails: refetch,
      getReservationsLimitInPeriodHandle,
    });
  };

  const onMarkShipmentConfirmDialogHandle = () => {
    shipmentReservationModalRef.current = showModal(AddTrackingNumberDialog, {
      modalRef: shipmentReservationModalRef,
      reservation: data,
      refetchReservationDetails: refetch,
    });
  };

  const onEditReservationDateAndTimeDialogHandle = () => {
    editDateAndTimeModalRef.current = showModal(EditReservationDateAndTimeDialog, {
      modalRef: editDateAndTimeModalRef,
      reservationDetail: data,
      refetchReservationDetails: refetch,
    });
  };

  const onCheckedInHandle = (checked: boolean) => {
    if (data?.id && checked) {
      updateReservationMutation.mutate({
        id: data?.id,
        status: UserUpdateStatus.checkedin,
      });
    }
  };

  const onCheckedInDialogHandle = () => {
    checkedInDialog.current = showModal(CheckedInConfirmDialog, {
      title: 'Are you sure you want check in?',
      subTitle:
        'The offer will become active again, and the user will be able to complete the reservation.',
      onConfirm: () => {
        onCheckedInHandle(true);
      },
    });
  };

  const onExtendPostingTimeDialogHandle = () => {
    extendPostingTimeDialog.current = showModal(ExtendPostingTimeDialog, {
      onConfirmationHandle: async (extendedDays) => {
        if (data?.id) {
          await updateReservationMutation.mutateAsync({
            id: data?.id,
            effectiveEndDateTime: dayjs(data?.effectiveEndDateTime)
              .add(extendedDays, 'days')
              .toISOString(),
          });
          enqueueSnackbar('Posting time extended successfully', { variant: 'success' });
        }
      },
    });
  };

  const onPaymentReleaseDialogHandle = () => {
    paymentReleaseDialog.current = showModal(PaymentConfirmDialog, {
      title: 'Are you sure you want to release the payment?',
      subTitle:
        'By releasing the payment, the user will receive the collab amount. This action cannot be undone.',
      onConfirm: () => {
        if (data?.inviteID) {
          paymentReleaseMutation.mutate({ reservationID: data?.id });
        }
      },
    });
  };

  const onPaymentStopDialogHandle = () => {
    paymentStopDialog.current = showModal(PaymentConfirmDialog, {
      title: 'Are you sure you want to stop the payment?',
      subTitle: 'A member of our team will review your case and contact you shortly via chat.',
      onConfirm: () => {
        if (data?.inviteID) {
          paymentStopMutation.mutate({ reservationID: data?.id });
        }
      },
      isStopPayment: true,
    });
  };

  const refreshHandle = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const postLinkHandle = React.useCallback(() => {
    window.open(data?.postLink || '', '_blank');
  }, [data?.postLink]);

  const handleSupport = () => {
    if (!(venueId && data?.id && data?.user)) {
      return;
    }
    if (setReservation && data) {
      setReservation(data);
    }
    setShouldChatOpen?.(prev => !prev);
  };

  const createConversationWithUser = async () => {
    try {
      setIsConversationCreationLoading(true);
      await venueGetConversationWithUserApi({
        venueID: venueId || '',
        userID: data?.userID || '',
        reservationID: data?.id || '',
        offerTitle: data?.offerTitle || '',
      });
      setIsConversationCreationLoading(false);
      navigate(
        `/chats?userID=${data?.userID}&reservationID=${data?.id}&offerTitle=${data?.offerTitle}`,
      );
    } catch (error) {
      console.error(error);
      setIsConversationCreationLoading(false);
      enqueueSnackbar('Something went wrong please try again later.', { variant: 'error' });
    }
  };

  const handleReservationRating = () => {
    showModal(ReservationRatingDialog, { reservation: data, userData });
  };

  const offerCategory = data?.offer?.category || data?.offerCategory;

  const isEditable =
    (offerCategory &&
      OfferCategoriesCallToConfirm.includes(offerCategory) &&
      data.status === ReservationStatus.reserved) ||
    false;

  const canReleasePayment = !!(
    data &&
    data.status === ReservationStatus.checkedin &&
    !data.requirementsSubmitted &&
    !data.paymentStatus &&
    isAfter48HoursOfOfferEnd(data)
  );

  const canStopPayment = !!(
    data &&
    data?.paymentStatus === ReservationPaymentStatus.ready &&
    data.status === ReservationStatus.checkedin &&
    data?.requirementsSubmitted
  );

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const isChatCanStart = React.useMemo(
    () =>
      (data &&
        subscriptionPlan === 'plus' &&
        checkChatHoursIsNotPassed(data) &&
        data?.status !== ReservationStatus.cancelled) ||
      (data && !!data?.inviteID && data.paymentStatus === ReservationPaymentStatus.stop) ||
      (data &&
        checkChatHoursIsNotPassed(data) &&
        data &&
        data?.status !== ReservationStatus.cancelled &&
        (!!data?.inviteID ||
          (offerCategory === OfferCategory.beauty && data?.offer?.confirmWithCall))) ||
      false,
    [subscriptionPlan, data, offerCategory],
  );

  const isOnlineBrand = venue?.tags?.includes(VENUE_MANUAL_TAGS.Online);
  const isProductCategory = data?.offer?.category === OfferCategory.product;
  const requirementsValues = getReservationRequirementsValues(data);

  const reservationHasRequirements = requirementsValues !== ExtendedOfferRequirements.noRequirement;
  const chatOpenTimeForProductReservations = !reservationHasRequirements
    ? dayjs(data?.shippedDate).add(7, 'day')
    : reservationHasRequirements && !data?.requirementsSubmitted
      ? dayjs(data?.shippedDate).add(16, 'day')
      : data?.requirementsSubmitted && data?.requirementsSubmittedDateTime
        ? dayjs(data?.requirementsSubmittedDateTime).add(4, 'day')
        : undefined;

  const isChatOpenForProductReservation: boolean | undefined =
    isProductCategory &&
    (data.shippingStatus === ShippingStatus.waiting ||
      (data.shippingStatus === ShippingStatus.shipped &&
        !!isAfterNow(chatOpenTimeForProductReservations)));

  const isShopifyReservation = React.useMemo(() => !!data?.offer?.shopifyProductID, [data]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: 'white',
        flex: 1,
        py: 4,
        pl: 10,
        pr: 6,
      }}
    >
      <FloatTopLeft>
        <GoBack />
      </FloatTopLeft>

      <Grid container spacing={4}>
        <Grid item lg={8} md={7} xs={7}>
          <Box
            sx={{
              borderRadius: '8px',
              border: '1px solid #F1F1F1',
              backgroundColor: '#fff',
              padding: '22px 32px',
            }}
          >
            <Box
              sx={{
                borderBottom: '1px solid #F7F7F7',
                paddingBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '24px',
              }}
            >
              <Typography fontSize={'20px'} fontWeight={600}>
                Reservation details
              </Typography>

              {data?.inviteID && (
                <CollabBadge>
                  <Icons.CollabOfferIcon style={{ width: '20px', height: '20px' }} /> Collab offer
                </CollabBadge>
              )}
            </Box>
            <Box my={4}>
              {isLoading ? (
                <>
                  <Stack spacing={3}>
                    {[...Array(6)].map((_, i) => (
                      <LabelDetail
                        key={i}
                        label={<Skeleton width='80%' />}
                        description={<Skeleton width='100%' />}
                      />
                    ))}
                  </Stack>
                  <Stack spacing={3} mt={6}>
                    {[...Array(3)].map((_, i) => (
                      <LabelDetail
                        key={i}
                        label={<Skeleton width='80%' />}
                        description={<Skeleton width='100%' />}
                      />
                    ))}
                  </Stack>
                </>
              ) : (
                <>
                  <Stack spacing={3}>
                    <LabelDetail
                      label='Offer'
                      description={
                        <Box
                          component={Link}
                          to={`/offers/${data?.offer?.id}`}
                          sx={{
                            textDecoration: 'none',
                            ':hover': {
                              textDecoration: 'underline',
                            },
                            color: '#1E55AA',
                          }}
                        >
                          {data?.offer?.title}
                        </Box>
                      }
                    />
                    <LabelDetail label='Description' description={data?.offer?.description} />
                    {data?.offer?.category !== OfferCategory.product && (
                      <LabelDetail
                        alignItems='center'
                        label='Offer time'
                        description={
                          offerStartTime && (
                            <DateTime
                              reservedDate={reservedDate}
                              value={offerStartTime ?? dayjs()}
                              endValue={offerEndTime}
                              showEditBtn={isEditable}
                              onEditHandle={onEditReservationDateAndTimeDialogHandle}
                            />
                          )
                        }
                      />
                    )}
                    <LabelDetail
                      alignItems='center'
                      label='Requirements'
                      description={
                        <Requirements
                          values={
                            isOnlineBrand
                              ? requirements.filter(
                                (requirement) => requirement.label !== 'includeSelfInPost',
                              )
                              : requirements
                          }
                        />
                      }
                    />
                    {isOnlineBrand && data?.offer?.category === OfferCategory.product && (
                      <LabelDetail
                        label={'Reservation date'}
                        description={getDateAndTimeInTimezoneInDayjs(
                          data?.reservationDate,
                          data?.timeZoneId,
                        )?.format('LL')}
                      />
                    )}
                    {!isOnlineBrand && (
                      <LabelDetail
                        label='Reservation Status'
                        description={
                          <StatusLabel
                            status={
                              data?.validation === ReservationReviewType.validated &&
                                data?.status === ReservationStatus.checkedin
                                ? ExtendedReservationStatus.completed
                                : data?.status
                            }
                            type='reservation'
                            emptyBackground
                          />
                        }
                      />
                    )}
                    {isOnlineBrand && data?.offer?.category === OfferCategory.product && (
                      <>
                        <LabelDetail
                          label={'User address'}
                          description={
                            data?.shippingAddress ? (
                              <Box display={'flex'} alignItems={'flex-start'} gap={'5px'}>
                                <Typography fontSize={'15px'}>{getAddressString(data)}</Typography>
                                <CopyToClipboard
                                  text={getAddressString(data)}
                                  onCopy={() => {
                                    enqueueSnackbar('Address copied to clipboard', {
                                      variant: 'success',
                                    });
                                  }}
                                >
                                  <Icons.CopyIcon style={{ cursor: 'pointer' }} />
                                </CopyToClipboard>
                              </Box>
                            ) : (
                              '-'
                            )
                          }
                        />
                        <LabelDetail
                          label={'Shipping status'}
                          description={
                            <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                              {data?.shippingStatus === ShippingStatus.waiting && (
                                <Icons.ShippmentIcon
                                  style={{ color: '#F3B821', height: '26px', width: '26px' }}
                                />
                              )}
                              <StatusLabel
                                type='shippingStatus'
                                status={data ? getReservationStatus(data) : ''}
                              />
                            </Box>
                          }
                        />
                        {isOnlineBrand &&
                          <LabelDetail
                            label={'Incentive'}
                            description={
                              <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                                <Typography fontSize={'15px'} fontWeight={400}>{data?.incentiveValue?.toFixed(2)}</Typography>
                              </Box>
                            }
                          />
                        }
                        <LabelDetail
                          label={'Tracking number'}
                          description={
                            data?.orderTracking?.trackingNumber ? (
                              <Box display={'flex'} alignItems={'center'} gap={'5px'}>
                                <Typography
                                  component={data?.orderTracking?.trackingUrl ? 'a' : 'span'}
                                  href={data?.orderTracking?.trackingUrl || '#'}
                                  sx={{ color: '#303030', fontSize: '15px' }}
                                >
                                  {data?.orderTracking?.trackingNumber}
                                </Typography>
                                <CopyToClipboard
                                  text={data?.orderTracking?.trackingNumber}
                                  onCopy={() => {
                                    enqueueSnackbar('Tracking number copied to clipboard', {
                                      variant: 'success',
                                    });
                                  }}
                                >
                                  <Icons.CopyIcon style={{ cursor: 'pointer' }} />
                                </CopyToClipboard>
                              </Box>
                            ) : (
                              '-'
                            )
                          }
                        />
                        <LabelDetail
                          label={isShopifyReservation ? 'Delivery date' : 'Shipping date'}
                          description={
                            data?.shippedDate
                              ? getDateAndTimeInTimezoneInDayjs(
                                data?.shippedDate,
                                data?.timeZoneId,
                              )?.format('LL')
                              : '-'
                          }
                        />
                        {data?.inviteID && (
                          <>
                            <LabelDetail
                              label='Cost'
                              description={<OfferPriceStatus reservation={data} />}
                            />
                            {data?.paymentStatus && (
                              <LabelDetail
                                label='Payment status'
                                description={
                                  data?.paymentStatus !== ReservationPaymentStatus.ready &&
                                    data?.paymentStatus !== ReservationPaymentStatus.stop ? (
                                    <StatusLabel
                                      status={data?.paymentStatus}
                                      type='paymentStatus'
                                      emptyBackground
                                    />
                                  ) : (
                                    <>
                                      {data?.paymentStatus === ReservationPaymentStatus.ready && (
                                        <Typography fontSize={'15px'} fontWeight={400}>
                                          The payment will be released within 48 hours
                                        </Typography>
                                      )}

                                      {data?.paymentStatus === ReservationPaymentStatus.stop && (
                                        <Typography
                                          fontSize={'15px'}
                                          fontWeight={500}
                                          color={'#37BB90'}
                                        >
                                          You stopped the payment. Our team is reviewing your
                                          request
                                        </Typography>
                                      )}
                                    </>
                                  )
                                }
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                    <Box position={'relative'}>
                      <LabelDetail
                        label='Post link'
                        description={
                          data && data?.postLink ? (
                            <ReservationPostLink reservation={data} detailPage />
                          ) : data?.status === ReservationStatus.checkedin &&
                            reservationHasRequirements ? (
                            <ReservationTimeLeftToPost
                              data={data}
                              showExtendBtn
                              isExtendButtonLoading={updateReservationMutation.isPending}
                              onExtendHandle={onExtendPostingTimeDialogHandle}
                            />
                          ) : (
                            '-'
                          )
                        }
                        alignItems='center'
                      />
                      {!!data?.rating && (
                        <LabelDetail
                          label='Rating'
                          description={
                            <Box display={'flex'} alignItems={'flex-end'} gap={'5px'}>
                              <Icons.StarIcon /> <span>{data.rating} Rating</span>
                            </Box>
                          }
                        />
                      )}
                    </Box>
                  </Stack>
                  {!isOnlineBrand ? (
                    <>
                      <Stack
                        spacing={3}
                        mt={6}
                        position={'relative'}
                        sx={{ borderTop: '1.5px solid #F7F7F7' }}
                        pt={3}
                      >
                        <LabelDetail label='Check in time' description={checkedInValue} />
                        {data?.inviteID && (
                          <LabelDetail
                            label='Cost'
                            description={<OfferPriceStatus reservation={data} />}
                          />
                        )}
                        {data?.offer?.private && (
                          <>
                            {data?.paymentStatus && (
                              <LabelDetail
                                label='Payment status'
                                description={
                                  data?.paymentStatus !== ReservationPaymentStatus.ready &&
                                    data?.paymentStatus !== ReservationPaymentStatus.stop ? (
                                    <StatusLabel
                                      status={data?.paymentStatus}
                                      type='paymentStatus'
                                      emptyBackground
                                    />
                                  ) : (
                                    <>
                                      {data?.paymentStatus === ReservationPaymentStatus.ready && (
                                        <Typography fontSize={'15px'} fontWeight={400}>
                                          The payment will be released within 48 hours
                                        </Typography>
                                      )}

                                      {data?.paymentStatus === ReservationPaymentStatus.stop && (
                                        <Typography
                                          fontSize={'15px'}
                                          fontWeight={500}
                                          color={'#37BB90'}
                                        >
                                          You stopped the payment. Our team is reviewing your
                                          request
                                        </Typography>
                                      )}
                                    </>
                                  )
                                }
                              />
                            )}
                          </>
                        )}
                      </Stack>
                      <Stack spacing={1} mt={10}>
                        <Box display={'flex'} alignItems={'center'} gap={'12px'}>
                          {isChatCanStart && (
                            <Button
                              size='large'
                              variant='contained'
                              sx={{ borderRadius: '5px', px: '36px' }}
                              startIcon={
                                isConversationCreationLoading ? (
                                  <CircularProgress size='1rem' sx={{ color: 'grey' }} />
                                ) : (
                                  <Icons.ChatIcon style={{ height: '20px', width: '20px' }} />
                                )
                              }
                              disabled={isConversationCreationLoading}
                              onClick={createConversationWithUser}
                            >
                              Chat
                            </Button>
                          )}
                          {data?.status === ReservationStatus.reserved && (
                            <StopPaymentButton
                              variant='outlined'
                              size='large'
                              sx={{ borderRadius: '5px' }}
                              onClick={onCancelReservationDialogHandle}
                            >
                              Cancel reservation
                            </StopPaymentButton>
                          )}

                          {data?.privateOffer && (
                            <>
                              {canReleasePayment && (
                                <ReleasePaymentButton
                                  variant='outlined'
                                  size='large'
                                  onClick={onPaymentReleaseDialogHandle}
                                  {...(paymentReleaseMutation.isPending
                                    ? {
                                      startIcon: (
                                        <CircularProgress size='1rem' sx={{ color: 'grey' }} />
                                      ),
                                      disabled: true,
                                    }
                                    : {})}
                                >
                                  Release payment
                                </ReleasePaymentButton>
                              )}

                              {canStopPayment && (
                                <StopPaymentButton
                                  variant='outlined'
                                  size='large'
                                  onClick={onPaymentStopDialogHandle}
                                  {...(paymentStopMutation.isPending
                                    ? {
                                      startIcon: (
                                        <CircularProgress size='1rem' sx={{ color: 'grey' }} />
                                      ),
                                      disabled: true,
                                    }
                                    : {})}
                                >
                                  Stop payment
                                </StopPaymentButton>
                              )}
                            </>
                          )}

                          {(data?.status === ReservationStatus.noshow ||
                            data?.status === ReservationStatus.noshowprocessed ||
                            (data?.status === ReservationStatus.reserved)) && (
                              <Button
                                size='large'
                                variant='outlined'
                                sx={{ borderRadius: '5px', px: '28px' }}
                                onClick={onCheckedInDialogHandle}
                              >
                                Check in
                              </Button>
                            )}

                          {data?.requirementsSubmitted && !data?.rating && (
                            <Button
                              size='large'
                              variant='contained'
                              color='primary'
                              sx={{ borderRadius: '5px', px: 4 }}
                              startIcon={<Icons.StarIcon />}
                              onClick={handleReservationRating}
                            >
                              Rate
                            </Button>
                          )}

                          <Button
                            size='large'
                            variant='outlined'
                            disabled={isLoading}
                            sx={{ borderRadius: '5px', px: '28px' }}
                            onClick={handleSupport}
                          >
                            Support
                          </Button>
                        </Box>
                      </Stack>
                    </>
                  ) : null}
                </>
              )}
              {data?.offer?.category === OfferCategory.product && (
                <>
                  <Stack spacing={1} mt={6}>
                    <Box display='flex' alignItems={'center'} gap={'16px'}>
                      {data.shippingStatus === ShippingStatus.waiting && !isShopifyReservation && (
                        <LoadingButton
                          variant='contained'
                          size='large'
                          sx={{ borderRadius: '5px', px: '36px' }}
                          onClick={onMarkShipmentConfirmDialogHandle}
                        >
                          Mark as Shipped
                        </LoadingButton>
                      )}
                      {isChatOpenForProductReservation && (
                        <Button
                          size='large'
                          variant='outlined'
                          sx={{ borderRadius: '5px', px: '24px' }}
                          startIcon={
                            isConversationCreationLoading ? (
                              <CircularProgress size='1rem' sx={{ color: 'grey' }} />
                            ) : (
                              <Icons.ChatIcon style={{ height: '20px', width: '20px' }} />
                            )
                          }
                          disabled={isConversationCreationLoading}
                          onClick={createConversationWithUser}
                        >
                          Chat
                        </Button>
                      )}
                      {data?.shippingStatus === ShippingStatus.shipped &&
                        !data?.orderTracking?.trackingNumber && (
                          <LoadingButton
                            size='large'
                            variant='outlined'
                            sx={{ borderRadius: '5px', px: '16px' }}
                            onClick={onMarkShipmentConfirmDialogHandle}
                          >
                            Add Tracking number
                          </LoadingButton>
                        )}
                      {(data?.status === ReservationStatus.reserved ||
                        data?.shippingStatus === ShippingStatus.waiting) && (
                          <LoadingButton
                            size='large'
                            variant='outlined'
                            sx={{ borderRadius: '5px', px: '16px' }}
                            onClick={onCancelReservationDialogHandle}
                          >
                            Cancel Reservation
                          </LoadingButton>
                        )}

                      <LoadingButton startIcon={<Icons.SupportIcon />} onClick={handleSupport}>
                        Support
                      </LoadingButton>

                      {data.shippingStatus !== ShippingStatus.waiting && (
                        <Box display={'flex'} alignItems={'center'} gap={'22px'}>
                          {data.requirementsSubmitted && !data?.rating && (
                            <Button
                              size='large'
                              variant='contained'
                              color='primary'
                              sx={{ borderRadius: '5px', px: 4 }}
                              startIcon={<Icons.StarIcon />}
                              onClick={handleReservationRating}
                            >
                              Rate
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item lg={4} md={5} xs={5}>
          <Box
            sx={{
              borderRadius: '8px',
              border: '1px solid #F1F1F1',
              backgroundColor: '#fff',
              padding: '22px 32px',
              height: '100%',
              position: 'relative',
            }}
          >
            <Box sx={{ borderBottom: '1px solid #F7F7F7', paddingBottom: '15px' }}>
              <Typography fontSize={'20px'} fontWeight={500} textAlign={'center'}>
                {isOnlineBrand ? 'Member detail' : 'Guest detail'}
              </Typography>
            </Box>

            <Box
              my={5}
              display={'flex'}
              justifyContent={'center'}
              flexDirection={'column'}
              alignItems={'center'}
            >
              <SkeletonWithChild
                variant='circular'
                loading={isLoading}
                sx={{
                  width: '288px',
                  height: '288px',
                }}
              >
                {!isLoading && data && (
                  <LazyAvatar
                    variant='circular'
                    alt={String(data?.user?.name)}
                    placeholderSrc={String(data?.userPicture?.thumbnail)}
                    src={String(data?.userPicture?.medium)}
                    sx={{
                      width: '288px',
                      height: '288px',
                    }}
                  />
                )}
              </SkeletonWithChild>

              <SkeletonWithChild loading={isLoading}>
                <Typography
                  fontWeight={500}
                  fontSize={'20px'}
                  mt={3}
                  mb={1}
                >{`${data?.user?.firstname} ${data?.user?.lastname}`}</Typography>
              </SkeletonWithChild>
              <SkeletonWithChild loading={isLoading}>
                <Typography component={Stack} direction='row' spacing={1}>
                  <InstagramTag username={data?.user?.instagramHandle} sx={{ ml: 1 }} />

                  <Box>·</Box>
                  <Box>
                    <Box component='span' fontWeight={400} fontSize={'15px'}>
                      {formatNumber(data?.user?.instagramFollowers ?? 0)}
                    </Box>
                    &nbsp;followers
                  </Box>
                </Typography>
              </SkeletonWithChild>
              <Box textAlign={'center'} mt={2}>
                <SaveUserButton
                  saved={Boolean(userData?.savedByVenue)}
                  userId={Number(data?.userID)}
                  sx={{ fontSize: '15px' }}
                >
                  Save profile
                </SaveUserButton>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReservationDetail;

const CollabBadge = styled.span`
  display: inline-block;
  background-color: #f6f6f6;
  color: #7b7bff;
  font-size: 15px;
  font-weight: 500;

  display: flex;
  align-items: center;
  border-radius: 8px;
  padding: 8px 16px;

  gap: 12px;
`;

const ChatWithUserBox = styled.div<{ backgroundColor?: string }>`
  border-radius: 10px;
  background-color: ${(props) => props.backgroundColor || '#fafafa'};

  padding: 14px 20px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 40px;
`;
